/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type FlowRunProgress, FlowRunState, type FlowRunStatus } from '@poweralpha/common';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSimulationProgressQuery } from '../api';
import {
  getOverallFraction,
  getPillPhase,
  getRunFraction,
  isRunActive,
  isRunFailed,
  isRunInProgress,
  QUEUED_STATES,
} from './progress';

const RECENT_LIMIT = 3;

const byCreatedDesc = (a: FlowRunStatus, b: FlowRunStatus) =>
  dayjs(b.created).valueOf() - dayjs(a.created).valueOf();

/**
 * The user's flow runs with their progress, plus the top-bar pill's phase. Runs are tracked
 * from the moment they are seen active, so the pill can report how they ended. The result
 * stays until the page reloads; a new run after everything finished starts a fresh count.
 */
export function useSimulationProgress() {
  const { data } = useSimulationProgressQuery();
  const runs = useMemo(() => [...(data?.runs ?? [])].sort(byCreatedDesc), [data?.runs]);
  const progressById = useMemo(
    () => new Map((data?.progress ?? []).map((progress) => [progress.run_id, progress])),
    [data?.progress],
  );

  const { runningRuns, queuedRuns, recentRuns } = useMemo(
    () => ({
      runningRuns: runs.filter(isRunInProgress),
      queuedRuns: runs.filter((run) => QUEUED_STATES.includes(run.state.type)),
      recentRuns: runs.filter((run) => !isRunActive(run)).slice(0, RECENT_LIMIT),
    }),
    [runs],
  );

  // The expected total can grow while a run is going; never let its bar move backwards.
  const maxFractionByIdRef = useRef(new Map<string, number>());
  const fractionById = useMemo(() => {
    const maxById = maxFractionByIdRef.current;
    return new Map(
      runningRuns.map((run): [string, number | null] => {
        const fraction = getRunFraction(progressById.get(run.id));
        if (fraction === null) return [run.id, maxById.get(run.id) ?? null];
        const monotonic = Math.max(maxById.get(run.id) ?? 0, fraction);
        maxById.set(run.id, monotonic);
        return [run.id, monotonic];
      }),
    );
  }, [runningRuns, progressById]);

  // A batch that stops reporting progress (cancelling, paused) keeps its case-count weight, so
  // the overall bar does not jump when its weight would otherwise fall back to 1.
  const weightByIdRef = useRef(new Map<string, number>());
  const weightById = useMemo(() => {
    const weights = weightByIdRef.current;
    for (const progress of progressById.values()) weights.set(progress.run_id, progress.weight);
    return weights;
  }, [progressById]);

  const overall = getOverallFraction(
    runningRuns.map((run) => ({
      fraction: fractionById.get(run.id) ?? null,
      weight: weightById.get(run.id) ?? 1,
    })),
  );

  const [trackedIds, setTrackedIds] = useState<ReadonlySet<string>>(() => new Set());
  const activeIdsKey = runs
    .filter(isRunActive)
    .map((run) => run.id)
    .join(',');
  useEffect(() => {
    if (!activeIdsKey) return;
    const activeIds = activeIdsKey.split(',');
    setTrackedIds((prev) => {
      if (activeIds.every((id) => prev.has(id))) return prev;
      // Runs that start after the tracked ones all finished begin a new summary.
      const isContinuing = activeIds.some((id) => prev.has(id));
      return new Set(isContinuing ? [...prev, ...activeIds] : activeIds);
    });
  }, [activeIdsKey]);

  const trackedRuns = useMemo(
    () => runs.filter((run) => trackedIds.has(run.id)),
    [runs, trackedIds],
  );

  return {
    phase: getPillPhase(trackedRuns, overall),
    overall,
    runningRuns,
    queuedRuns,
    recentRuns,
    progressById: progressById as ReadonlyMap<string, FlowRunProgress>,
    fractionById: fractionById as ReadonlyMap<string, number | null>,
    failedCount: trackedRuns.filter(isRunFailed).length,
    doneCount: trackedRuns.filter((run) => run.state.type === FlowRunState.COMPLETED).length,
    cancelledCount: trackedRuns.filter((run) => run.state.type === FlowRunState.CANCELLED).length,
  };
}
